// Controlled vocabularies — small lookup tables maintained by admin tools,
// not by user input. These const arrays are the SINGLE source of truth and
// also seed the D1 vocab tables (the seed/vocab.ts script imports from here).
// Slugs are stable identifiers; names are display strings.

// ---------------------------------------------------------------------------
// ArtForm — 52 entries, grouped by discipline. Reconciled against the WWAAVE
// creative-types list (2026-08-03). `production` covers the crew disciplines,
// which are people a host can host even though they don't perform.
//
// Retired slugs are NOT parked here: migration 0046 remaps every existing
// selection onto its successor and removes the old rows, so this array is the
// whole of the vocabulary.
// ---------------------------------------------------------------------------

export const ART_FORM_GROUPS = [
  'performance',
  'visual',
  'workshop',
  'production',
  'multidisciplinary',
] as const;
export type ArtFormGroup = (typeof ART_FORM_GROUPS)[number];

/** Display labels for grouped selects. Keep in step with ART_FORM_GROUPS. */
export const ART_FORM_GROUP_LABELS: Readonly<Record<ArtFormGroup, string>> = {
  performance: 'Performance',
  visual: 'Visual',
  workshop: 'Workshop',
  production: 'Production and crew',
  multidisciplinary: 'Multidisciplinary',
};

export type ArtFormEntry = {
  slug: string;
  name: string;
  group: ArtFormGroup;
  display_order: number;
};

export const ART_FORMS: readonly ArtFormEntry[] = [
  // performance — music
  { slug: 'solo_musician', name: 'Solo musician', group: 'performance', display_order: 10 },
  { slug: 'singer_songwriter', name: 'Singer-songwriter', group: 'performance', display_order: 20 },
  { slug: 'vocalist', name: 'Vocalist', group: 'performance', display_order: 30 },
  { slug: 'instrumentalist', name: 'Instrumentalist', group: 'performance', display_order: 40 },
  { slug: 'session_musician', name: 'Session musician', group: 'performance', display_order: 50 },
  { slug: 'live_band', name: 'Live band', group: 'performance', display_order: 60 },
  { slug: 'dj', name: 'DJ', group: 'performance', display_order: 80 },
  { slug: 'selector', name: 'Selector', group: 'performance', display_order: 85 },
  { slug: 'live_electronic', name: 'Live electronic', group: 'performance', display_order: 90 },
  { slug: 'mc', name: 'MC', group: 'performance', display_order: 100 },
  { slug: 'beatboxer', name: 'Beatboxer', group: 'performance', display_order: 120 },
  { slug: 'experimental_artist', name: 'Experimental artist', group: 'performance', display_order: 130 },
  // performance — non-music
  { slug: 'comedian', name: 'Comedian', group: 'performance', display_order: 140 },
  { slug: 'storyteller', name: 'Storyteller', group: 'performance', display_order: 150 },
  { slug: 'poet_spoken_word', name: 'Poet / spoken word', group: 'performance', display_order: 160 },
  { slug: 'dancer', name: 'Dancer', group: 'performance', display_order: 170 },
  { slug: 'circus_performer', name: 'Circus performer', group: 'performance', display_order: 180 },
  { slug: 'aerialist', name: 'Aerialist', group: 'performance', display_order: 190 },
  { slug: 'fire_performer', name: 'Fire performer', group: 'performance', display_order: 200 },
  { slug: 'clown', name: 'Clown', group: 'performance', display_order: 210 },
  { slug: 'magician', name: 'Magician', group: 'performance', display_order: 220 },
  { slug: 'puppeteer', name: 'Puppeteer', group: 'performance', display_order: 230 },
  { slug: 'drag_performer', name: 'Drag performer', group: 'performance', display_order: 240 },
  { slug: 'burlesque_performer', name: 'Burlesque performer', group: 'performance', display_order: 250 },
  { slug: 'performance_artist', name: 'Performance artist', group: 'performance', display_order: 260 },
  // visual
  { slug: 'visual_artist', name: 'Visual artist', group: 'visual', display_order: 300 },
  { slug: 'painter', name: 'Painter', group: 'visual', display_order: 310 },
  { slug: 'muralist', name: 'Muralist', group: 'visual', display_order: 320 },
  { slug: 'illustrator', name: 'Illustrator', group: 'visual', display_order: 330 },
  { slug: 'sculptor', name: 'Sculptor', group: 'visual', display_order: 340 },
  { slug: 'ceramicist', name: 'Ceramicist', group: 'visual', display_order: 350 },
  { slug: 'textile_artist', name: 'Textile artist', group: 'visual', display_order: 360 },
  { slug: 'woodworker', name: 'Woodworker', group: 'visual', display_order: 370 },
  { slug: 'maker', name: 'Maker', group: 'visual', display_order: 380 },
  { slug: 'installation_artist', name: 'Installation artist', group: 'visual', display_order: 390 },
  { slug: 'florist_installation', name: 'Florist / installation', group: 'visual', display_order: 400 },
  { slug: 'photographer', name: 'Photographer', group: 'visual', display_order: 410 },
  { slug: 'filmmaker', name: 'Filmmaker', group: 'visual', display_order: 420 },
  { slug: 'vj_projection_artist', name: 'VJ / projection artist', group: 'visual', display_order: 430 },
  { slug: 'tattoo_artist', name: 'Tattoo artist', group: 'visual', display_order: 440 },
  { slug: 'chef_food_artist', name: 'Chef / food artist', group: 'visual', display_order: 450 },
  // workshop
  { slug: 'workshop_facilitator', name: 'Workshop facilitator', group: 'workshop', display_order: 500 },
  { slug: 'writer', name: 'Writer', group: 'workshop', display_order: 510 },
  { slug: 'yoga_movement_teacher', name: 'Yoga / movement teacher', group: 'workshop', display_order: 520 },
  { slug: 'community_organiser', name: 'Community organiser', group: 'workshop', display_order: 530 },
  // production and crew
  { slug: 'sound_engineer', name: 'Sound engineer', group: 'production', display_order: 600 },
  { slug: 'lighting_design', name: 'Lighting design', group: 'production', display_order: 610 },
  { slug: 'stage_hand', name: 'Stage hand', group: 'production', display_order: 620 },
  { slug: 'production_crew', name: 'Production crew', group: 'production', display_order: 630 },
  { slug: 'creative_helper', name: 'Creative helper', group: 'production', display_order: 640 },
  // multidisciplinary
  { slug: 'multidisciplinary', name: 'Multidisciplinary', group: 'multidisciplinary', display_order: 700 },
  { slug: 'other', name: 'Other', group: 'multidisciplinary', display_order: 9999 },
] as const;

export type ArtForm = (typeof ART_FORMS)[number]['slug'];
export const ART_FORM_SLUGS = ART_FORMS.map((a) => a.slug) as readonly ArtForm[];

// ---------------------------------------------------------------------------
// VenueType — 48 entries, reconciled 2026-08-03. `group` was added in the same
// pass so venue types can be bucketed in browse filters the way art forms are.
//
// Seven slugs were retired (eco_lodge, boutique_hotel, bookstore, yoga_shala,
// coworking, community_hall, unishop). Unlike art forms they are DISABLED, not
// deleted — see migration 0046.
// ---------------------------------------------------------------------------

export const VENUE_TYPE_GROUPS = [
  'hospitality',
  'music_nightlife',
  'arts_culture',
  'community',
  'accommodation',
  'outdoor',
  'event_space',
] as const;
export type VenueTypeGroup = (typeof VENUE_TYPE_GROUPS)[number];

/** Display labels for grouped selects. Keep in step with VENUE_TYPE_GROUPS. */
export const VENUE_TYPE_GROUP_LABELS: Readonly<Record<VenueTypeGroup, string>> = {
  hospitality: 'Food and drink',
  music_nightlife: 'Music and nightlife',
  arts_culture: 'Arts and culture',
  community: 'Community',
  accommodation: 'Stays',
  outdoor: 'Outdoors and on the road',
  event_space: 'Event spaces',
};

export type VenueTypeEntry = {
  slug: string;
  name: string;
  group: VenueTypeGroup;
  display_order: number;
};

export const VENUE_TYPES: readonly VenueTypeEntry[] = [
  // hospitality
  { slug: 'bar', name: 'Bar', group: 'hospitality', display_order: 10 },
  { slug: 'beach_bar', name: 'Beach bar', group: 'hospitality', display_order: 20 },
  { slug: 'cocktail_bar', name: 'Cocktail bar', group: 'hospitality', display_order: 30 },
  { slug: 'rooftop_bar', name: 'Rooftop bar', group: 'hospitality', display_order: 40 },
  { slug: 'wine_bar', name: 'Wine bar', group: 'hospitality', display_order: 50 },
  { slug: 'listening_bar', name: 'Listening bar', group: 'hospitality', display_order: 60 },
  { slug: 'pub', name: 'Pub', group: 'hospitality', display_order: 70 },
  { slug: 'brewery', name: 'Brewery', group: 'hospitality', display_order: 80 },
  { slug: 'cafe', name: 'Café', group: 'hospitality', display_order: 90 },
  { slug: 'restaurant', name: 'Restaurant', group: 'hospitality', display_order: 100 },
  // music and nightlife
  { slug: 'live_music_venue', name: 'Live music venue', group: 'music_nightlife', display_order: 150 },
  { slug: 'club', name: 'Club', group: 'music_nightlife', display_order: 160 },
  { slug: 'nightclub', name: 'Nightclub', group: 'music_nightlife', display_order: 170 },
  { slug: 'comedy_club', name: 'Comedy club', group: 'music_nightlife', display_order: 180 },
  { slug: 'house_concert', name: 'House concert', group: 'music_nightlife', display_order: 190 },
  { slug: 'living_room', name: 'Living room', group: 'music_nightlife', display_order: 195 },
  // arts and culture
  { slug: 'gallery', name: 'Gallery', group: 'arts_culture', display_order: 250 },
  { slug: 'theatre', name: 'Theatre', group: 'arts_culture', display_order: 260 },
  { slug: 'cultural_centre', name: 'Cultural centre', group: 'arts_culture', display_order: 270 },
  { slug: 'studio', name: 'Studio', group: 'arts_culture', display_order: 280 },
  { slug: 'makerspace', name: 'Makerspace', group: 'arts_culture', display_order: 290 },
  { slug: 'bookshop', name: 'Bookshop', group: 'arts_culture', display_order: 300 },
  { slug: 'record_store', name: 'Record store', group: 'arts_culture', display_order: 310 },
  { slug: 'shop', name: 'Shop', group: 'arts_culture', display_order: 320 },
  // community
  { slug: 'community_space', name: 'Community space', group: 'community', display_order: 380 },
  { slug: 'co_op', name: 'Co-op', group: 'community', display_order: 390 },
  { slug: 'church', name: 'Church', group: 'community', display_order: 400 },
  { slug: 'climbing_gym', name: 'Climbing gym', group: 'community', display_order: 410 },
  // accommodation
  { slug: 'hotel', name: 'Hotel', group: 'accommodation', display_order: 470 },
  { slug: 'resort', name: 'Resort', group: 'accommodation', display_order: 480 },
  { slug: 'guesthouse', name: 'Guesthouse', group: 'accommodation', display_order: 490 },
  { slug: 'bnb', name: 'B&B', group: 'accommodation', display_order: 500 },
  { slug: 'homestay', name: 'Homestay', group: 'accommodation', display_order: 510 },
  { slug: 'hostel', name: 'Hostel', group: 'accommodation', display_order: 520 },
  { slug: 'backpackers', name: 'Backpackers', group: 'accommodation', display_order: 530 },
  { slug: 'eco_retreat', name: 'Eco-retreat', group: 'accommodation', display_order: 540 },
  { slug: 'wellness_yoga_space', name: 'Wellness & yoga space', group: 'accommodation', display_order: 550 },
  // outdoor
  { slug: 'campground', name: 'Campground', group: 'outdoor', display_order: 600 },
  { slug: 'caravan_park', name: 'Caravan park', group: 'outdoor', display_order: 610 },
  { slug: 'glamping_site', name: 'Glamping site', group: 'outdoor', display_order: 620 },
  { slug: 'farmstay', name: 'Farmstay', group: 'outdoor', display_order: 630 },
  { slug: 'boat', name: 'Boat', group: 'outdoor', display_order: 640 },
  { slug: 'cruise_ship', name: 'Cruise ship', group: 'outdoor', display_order: 650 },
  // event spaces
  { slug: 'warehouse', name: 'Warehouse', group: 'event_space', display_order: 700 },
  { slug: 'pop_up_space', name: 'Pop-up space', group: 'event_space', display_order: 710 },
  { slug: 'boutique_festival', name: 'Boutique festival', group: 'event_space', display_order: 720 },
  { slug: 'wedding_venue', name: 'Wedding venue', group: 'event_space', display_order: 730 },
  { slug: 'other', name: 'Other', group: 'event_space', display_order: 9999 },
] as const;

export type VenueType = (typeof VENUE_TYPES)[number]['slug'];
export const VENUE_TYPE_SLUGS = VENUE_TYPES.map((v) => v.slug) as readonly VenueType[];

// ---------------------------------------------------------------------------
// VibeTag — approximately 12 entries.
// ---------------------------------------------------------------------------

export type VibeTagEntry = { slug: string; name: string; display_order: number };

export const VIBE_TAGS: readonly VibeTagEntry[] = [
  { slug: 'intimate', name: 'Intimate', display_order: 10 },
  { slug: 'raucous', name: 'Raucous', display_order: 20 },
  { slug: 'contemplative', name: 'Contemplative', display_order: 30 },
  { slug: 'family_friendly', name: 'Family-friendly', display_order: 40 },
  { slug: 'late_night', name: 'Late-night', display_order: 50 },
  { slug: 'listening_room', name: 'Listening room', display_order: 60 },
  { slug: 'dancing_encouraged', name: 'Dancing encouraged', display_order: 70 },
  { slug: 'outdoor', name: 'Outdoor', display_order: 80 },
  { slug: 'candle_lit', name: 'Candle-lit', display_order: 90 },
  { slug: 'all_ages', name: 'All ages', display_order: 100 },
  { slug: 'eighteen_plus', name: '18+', display_order: 110 },
  { slug: 'accessible', name: 'Accessible', display_order: 120 },
] as const;

export type VibeTag = (typeof VIBE_TAGS)[number]['slug'];
export const VIBE_TAG_SLUGS = VIBE_TAGS.map((v) => v.slug) as readonly VibeTag[];

// ---------------------------------------------------------------------------
// Amenities — scoped, approximately 50 entries across scopes.
// ---------------------------------------------------------------------------

export const AMENITY_SCOPES = ['sound_tech', 'sleeping', 'food', 'drink', 'facilities'] as const;
export type AmenityScope = (typeof AMENITY_SCOPES)[number];

export type AmenityEntry = {
  slug: string;
  name: string;
  scope: AmenityScope;
  display_order: number;
};

export const AMENITIES: readonly AmenityEntry[] = [
  // sound_tech
  { slug: 'pa', name: 'PA system', scope: 'sound_tech', display_order: 10 },
  { slug: 'mic', name: 'Microphones', scope: 'sound_tech', display_order: 20 },
  { slug: 'drumkit', name: 'Drumkit', scope: 'sound_tech', display_order: 30 },
  { slug: 'bass_amp', name: 'Bass amp', scope: 'sound_tech', display_order: 40 },
  { slug: 'guitar_amp', name: 'Guitar amp', scope: 'sound_tech', display_order: 50 },
  { slug: 'mixer', name: 'Mixer', scope: 'sound_tech', display_order: 60 },
  { slug: 'in_ear_monitors', name: 'In-ear monitors', scope: 'sound_tech', display_order: 70 },
  { slug: 'stage_lights', name: 'Stage lights', scope: 'sound_tech', display_order: 80 },
  { slug: 'acoustic_only', name: 'Acoustic only', scope: 'sound_tech', display_order: 90 },
  // sleeping
  { slug: 'spare_room', name: 'Spare room', scope: 'sleeping', display_order: 10 },
  { slug: 'couch', name: 'Couch', scope: 'sleeping', display_order: 20 },
  { slug: 'camp_space', name: 'Camp space', scope: 'sleeping', display_order: 30 },
  { slug: 'multiple_rooms', name: 'Multiple rooms', scope: 'sleeping', display_order: 40 },
  { slug: 'offsite_recommended', name: 'Off-site recommended', scope: 'sleeping', display_order: 50 },
  // food
  { slug: 'breakfast', name: 'Breakfast', scope: 'food', display_order: 10 },
  { slug: 'lunch', name: 'Lunch', scope: 'food', display_order: 20 },
  { slug: 'dinner', name: 'Dinner', scope: 'food', display_order: 30 },
  { slug: 'snacks', name: 'Snacks', scope: 'food', display_order: 40 },
  { slug: 'communal_meal', name: 'Communal meal', scope: 'food', display_order: 50 },
  { slug: 'vegan_friendly', name: 'Vegan-friendly', scope: 'food', display_order: 60 },
  { slug: 'vegetarian_friendly', name: 'Vegetarian-friendly', scope: 'food', display_order: 70 },
  { slug: 'gf_friendly', name: 'Gluten-free-friendly', scope: 'food', display_order: 80 },
  // drink
  { slug: 'coffee', name: 'Coffee', scope: 'drink', display_order: 10 },
  { slug: 'tea', name: 'Tea', scope: 'drink', display_order: 20 },
  { slug: 'beer_on_tap', name: 'Beer on tap', scope: 'drink', display_order: 30 },
  { slug: 'wine', name: 'Wine', scope: 'drink', display_order: 40 },
  { slug: 'spirits', name: 'Spirits', scope: 'drink', display_order: 50 },
  { slug: 'non_alcoholic', name: 'Non-alcoholic', scope: 'drink', display_order: 60 },
  { slug: 'byo', name: 'BYO', scope: 'drink', display_order: 70 },
  // facilities
  { slug: 'wifi', name: 'Wifi', scope: 'facilities', display_order: 10 },
  { slug: 'parking', name: 'Parking', scope: 'facilities', display_order: 20 },
  { slug: 'kitchen_access', name: 'Kitchen access', scope: 'facilities', display_order: 30 },
  { slug: 'laundry', name: 'Laundry', scope: 'facilities', display_order: 40 },
  { slug: 'shower', name: 'Shower', scope: 'facilities', display_order: 50 },
  { slug: 'wheelchair_access', name: 'Wheelchair access', scope: 'facilities', display_order: 60 },
  { slug: 'outdoor_space', name: 'Outdoor space', scope: 'facilities', display_order: 70 },
  { slug: 'quiet_room', name: 'Quiet room', scope: 'facilities', display_order: 80 },
  { slug: 'bicycle_storage', name: 'Bicycle storage', scope: 'facilities', display_order: 90 },
] as const;

export type Amenity = (typeof AMENITIES)[number]['slug'];
export const AMENITY_SLUGS = AMENITIES.map((a) => a.slug) as readonly Amenity[];

export const SOUND_TECH_AMENITIES = AMENITIES.filter((a) => a.scope === 'sound_tech');
export const SLEEPING_AMENITIES = AMENITIES.filter((a) => a.scope === 'sleeping');
export const FOOD_AMENITIES = AMENITIES.filter((a) => a.scope === 'food');
export const DRINK_AMENITIES = AMENITIES.filter((a) => a.scope === 'drink');
export const FACILITIES_AMENITIES = AMENITIES.filter((a) => a.scope === 'facilities');

// ---------------------------------------------------------------------------
// Country — supported-country list at launch (AU + IDN per PRD §3 / Strategic
// Recs §6/§11). Other countries enabled later.
// ---------------------------------------------------------------------------

export type CountryEntry = { code: string; name: string; enabled: boolean };

export const COUNTRIES: readonly CountryEntry[] = [
  { code: 'AU', name: 'Australia', enabled: true },
  { code: 'ID', name: 'Indonesia', enabled: true },
  { code: 'NZ', name: 'New Zealand', enabled: false },
  { code: 'GB', name: 'United Kingdom', enabled: false },
  { code: 'US', name: 'United States', enabled: false },
] as const;

export const SUPPORTED_COUNTRY_CODES = COUNTRIES.filter((c) => c.enabled).map((c) => c.code);
export type SupportedCountryCode = (typeof SUPPORTED_COUNTRY_CODES)[number];

// ---------------------------------------------------------------------------
// Helper lookups
// ---------------------------------------------------------------------------

export function isValidArtForm(slug: string): slug is ArtForm {
  return (ART_FORM_SLUGS as readonly string[]).includes(slug);
}

export function isValidVenueType(slug: string): slug is VenueType {
  return (VENUE_TYPE_SLUGS as readonly string[]).includes(slug);
}

export function isValidVibeTag(slug: string): slug is VibeTag {
  return (VIBE_TAG_SLUGS as readonly string[]).includes(slug);
}

export function isValidAmenity(slug: string, scope?: AmenityScope): slug is Amenity {
  if (scope === undefined) return (AMENITY_SLUGS as readonly string[]).includes(slug);
  return AMENITIES.some((a) => a.slug === slug && a.scope === scope);
}
